import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { CompiereDataFieldType, DataStore, DataStoreStatus } from '@compiere-ws/models/compiere-data-json';
import { ValidationDisplayDirective } from '@iupics-components/directives/validation-display.directive';
import { ModalDisplayRequest } from '@iupics-components/models/ui-request';
import PrimeAutocompleteComponent from '@iupics-components/overrided/prime-autocomplete/prime-autocomplete.component';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import FieldConflictPanelComponent from '../field-conflict-panel/field-conflict-panel.component';
import ProcessUiComponent from '@iupics-components/specific/window/process-ui/process-ui.component';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import { FilterCtxService } from '@iupics-components/standard/grid/filters/services/filter-ctx.service';
import { GridTabInfinityScrollHeadlessUiComponent } from '@iupics-components/standard/grid/grid-tab-infinity-scroll-ui/grid-tab-infinity-scroll-ui.component';
import { ViewColumnsService } from '@iupics-components/standard/grid/services/view-columns.service';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import UniversalFilterUiComponent from '@iupics-components/standard/menu/universal-filter-ui/universal-filter-ui.component';
import ValuePreferencePanelComponent from '@iupics-components/standard/value-preference-panel/value-preference-panel.component';
import { AppConfig } from '@iupics-config/app.config.service';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { AbstractDataContainer, AbstractDataContainerCallout } from '@iupics-manager/models/abstract-datacontainer';
import { DynamicComponent } from '@iupics-manager/models/dynamic-component';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsDataField, IupicsZoomInfo } from '@iupics-manager/models/iupics-data';
import { IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { Utils } from '@iupics-util/tools/util';
import { provideGridApiService, providePanelService, ToolbarComponent } from '@iupics/apiz-grid';
import { TranslateService } from '@ngx-translate/core';
import { WorkspaceService } from '@web-desktop/components/workspace/components/workspace-ui/workspace.service';
import { UrlParamsService } from '@web-desktop/controllers/url-params.service';
import { cloneDeep, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'iu-autocomplete-ui',
  templateUrl: './autocomplete-ui.component.html',
  styleUrls: ['./autocomplete-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    PrimeAutocompleteComponent,
    NgClass,
    PrimeOverlayComponent,
    FieldConflictPanelComponent,
    forwardRef(() => ValuePreferencePanelComponent),
    forwardRef(() => ModalUiComponent),
    UniversalFilterUiComponent,
    GridTabInfinityScrollHeadlessUiComponent,
    ToolbarComponent,
    ValidationDisplayDirective,
  ],
  providers: [
    provideGridApiService(), // Provide the grid API service
    providePanelService(), // Provide the panel service
    FilterCtxService, // Provide the filterCtx service at this level and not root level
    ViewColumnsService, // Provide the tabColumns service at this level and not root level
  ],
})
export default class AutocompleteUiComponent extends AbstractDataContainer implements OnInit, AfterViewInit, OnDestroy {
  private static readonly URL_LIST_SEARCH_CACHE_LIMIT = 20;

  readonly #translateService = inject(TranslateService);
  /** shown in the list when a search finds nothing (an empty list looked broken) */
  get noResultLabel(): string {
    return this.#translateService.instant('generic.noResult');
  }
  readonly #config = inject(AppConfig);
  readonly #urlParamsService = inject(UrlParamsService);
  readonly #workspaceService = inject(WorkspaceService);
  readonly #cacheManagerService = inject(CacheManagerService);
  // Suggestions arrive asynchronously: mark the view dirty so an OnPush host (e.g. a grid toolbar filter chip) renders them
  readonly #cdr = inject(ChangeDetectorRef);

  // bounded per-field cache of already-fetched urlList search results, keyed by entityId + query + validationCode
  readonly #urlListSearchCache = new Map<string, any[]>();

  component = 'AutocompleteUiComponent';
  itemData: DynamicComponent;
  suggestionsFilter: any;
  @ViewChild('autoComplete', { static: true })
  autoComplete: PrimeAutocompleteComponent;
  @Input() suggestions: any[];
  @Input() scrollHeight = '240px';
  @Input() columnName: string;
  @Input() iconClass: string;
  @Input() limitSuggestions = 19;
  @Input() isAdvancedSearch = false;
  @Input() isAccountSearch = false;
  @Input() readonly = false;
  @Input() disable = false;
  @Input() multiple = false;
  @Input() isEditorView = false;
  @Input() isInsideOverflow = false;
  @Input() placeholder = '';
  @Input() data: IupicsDataField;

  styleLess = input(false);

  subscription: Subscription;
  isSetMandatoryValue = false;

  zoomInfo: IupicsZoomInfo;
  private searchTimeout: NodeJS.Timeout;

  @Output() autoCompleteEmitter = new EventEmitter<any>();

  @Output() checkGridEditorEmitter = new EventEmitter<any>();
  @Output() blurEmitter = new EventEmitter<any>();
  top: number;
  left: number;
  width: number;
  itemSuggestionsCache = null;

  @Input()
  set fieldValue(value: any) {
    this.autoComplete.value = value;
    this.autoComplete.updateInputField();
    if (this.DOMParentComponent?.isAccordion) {
      this.DOMParentComponent.setMandatory();
    }
  }

  get fieldValue() {
    return this.autoComplete.value;
  }

  dataContainers: AbstractDataContainerCallout;
  allDataRecovered = false;
  @Input() dataStoredEditor: DataStore;
  @ViewChild('opConflict', { static: true })
  opConflict: FieldConflictPanelComponent;

  // searchPanel to append
  parentContainer;
  searchPanelValidation: string;
  displaySearch = false;
  // formPanel to append
  displayFormUI = false;
  customFormModalBodyCss = undefined;
  specificWindowTitle: string;
  @ViewChild('vcrSpecific', { read: ViewContainerRef, static: false })
  vcrSpecific: ViewContainerRef;
  @ViewChild('specificModal', { read: ModalUiComponent, static: false })
  specificModal: ModalUiComponent;

  @Input() appendTo: any = 'body';

  filterCtx = inject(FilterCtxService);
  isProcessingFilterResponse = false;
  @ViewChild('gridTab', { static: false }) gridTab: GridTabInfinityScrollHeadlessUiComponent;

  standaloneCtx = input<DataStore>(undefined);

  isAutocompleteField = true;

  preventZoom = signal(false);

  ngOnInit(): void {
    if (this.isEditorView && this.dataStoredEditor) {
      this.dataStored = this.dataStoredEditor;
    }
    super.ngOnInit();
    this.cssGrid = this.cssClass;
    this.updateLabel();
    this.updateFieldType();
    this.updateAdvancedSearch();
    this.updateAccountSearch();
    this.updateFieldTypeForm();
    this.updateStandalone();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    let parentComp = this.DOMParentComponent;
    while (parentComp && !(parentComp instanceof EditTabUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
    if (parentComp instanceof EditTabUiComponent) {
      this.dataContainers = new AbstractDataContainerCallout(parentComp.dataContainers);
    }
    // ? à commenter si on ne veut pas charger les données d'une form pour affichage instantanné au clic.
    if (!Global.isMobile() && this.autoComplete && this.autoComplete.checkSpecificView && this.autoComplete.formId) {
      this.subscriptions.push(this.uiCreatorService.getSpecificWindow(this.autoComplete.formId).subscribe());
    }
    this.checkFocus();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscription?.unsubscribe();
  }

  checkFocus(): void {
    if (
      this.data?.IsDefaultFocus &&
      !this.isReadOnly &&
      this.editViewParent?.editTabs?.[0]?.dataStored?.status === DataStoreStatus.NEWRECORD &&
      (this.fieldValue === null || this.fieldValue === undefined)
    ) {
      this.autoComplete.inputEL?.nativeElement.focus();
    }
  }
  /**
   * Modifie la mandatory du champ
   * @param dataStored
   */
  updateMandatory(dataStored: DataStore, changedColumns?: any): void {
    const datastore = dataStored ? dataStored : this.dataStored;
    this.isFieldMandatory(datastore, changedColumns).subscribe((isMandatory) => {
      this.isMandatory = isMandatory;

      if (this.isMandatory || this.data?.isMandatory) {
        this.handleMandatoryField();
      } else {
        this.handleNonMandatoryField();
      }

      if (this.autoComplete) {
        this.autoComplete.setMandatoryCss(this.mandatoryCss);
      }
    });
  }

  dataChange(value): void {
    if (this.multiple && !Array.isArray(value)) {
      this.fieldValue = this.fieldValue ?? [];
      value = [...(cloneDeep(this.fieldValue) as any[]), value];
    }
    super.dataChange(value);
    //handle destruction during datachange
    if (this.autoCompleteEmitter) {
      this.autoCompleteEmitter.emit(this.fieldValue);
    }
    this.checkLabelClick();
  }

  private checkLabelClick(): void {
    this.preventZoom.set(this.isReadOnly && !this.fieldValue);
    this.preventContextMenu.set(this.preventZoom());
  }

  /**
   * Update le store avec ce qu'on reçoit du search panel
   * @param id id qu'on reçoit depuis l'emitter de ui-prime-autocomplete
   */
  setSearchSelectItem(event): void {
    this.isProcessingFilterResponse = true;
    let id = event.data['Data_UUID'];
    if (id) {
      id = id.split(',')[1];
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const validation = LogicEvaluator.replaceVariables(
      this.data.validationCode,
      this.connectorService.getIupicsUserContext(),
      this.getCurrentContext(),
      false,
      false,
      !this.isInUniversalFilter,
      this.editViewService?.getDataColumns('referenceID')
    );
    const entityId = this.fieldType === CompiereDataFieldType.FIELD ? this.data.fieldId : this.data.columnId;
    this.subscription = this.store
      .getAutocompleteDataById(this.fieldType, entityId, id, validation)
      .subscribe((dataWS) => {
        this.dataChange(dataWS[0]);
        this.isProcessingFilterResponse = false;
        this.hideSearchPanel();
        this.#cdr.markForCheck();
      });
  }
  /**
   * @description get text value linked to an id
   * @param id id received from url
   */
  getSearchItem(idValue: any): Observable<any> {
    const id = parseInt(idValue, 10) ? parseInt(idValue, 10) : idValue;
    const entityId = this.fieldType === CompiereDataFieldType.FIELD ? this.data.fieldId : this.data.columnId;
    return this.store
      .getAutocompleteDataById(
        this.fieldType,
        entityId,
        id,
        LogicEvaluator.replaceVariables(
          this.data.validationCode,
          this.connectorService.getIupicsUserContext(),
          this.getCurrentContext(),
          false,
          false,
          !this.isInUniversalFilter,
          this.editViewService?.getDataColumns('referenceID')
        )
      )
      .pipe(map((dataWS) => dataWS.find((data) => data.id === id)));
  }

  /**
   * Lancé au focus du champ ui-prime-autocomplete
   * @param event
   */
  search(event: { originalEvent: Event; query: string }, forceRefresh = false): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = undefined;
    }
    this.searchTimeout = setTimeout(
      () => {
        this.searchTimeout = undefined;
        this.handleSearch(event, forceRefresh);
      },
      // typing is already debounced once by iu-prime-autocomplete ([delay]="searchDelay")
      0
    );
  }

  /** one debounce for typing: remote lists wait a little, local lists filter at once */
  get searchDelay(): number {
    return this.data?.urlList ? 250 : 0;
  }

  selectByIdentifier(identifier: string, value: any): void {
    if (identifier && value && this.data.items) {
      for (const element of this.data.items) {
        const propose = element;
        if (propose[identifier] === value) {
          this.fieldValue = propose;
        }
      }
    }
  }

  zoomAcross(): void {
    this.isZoom = true;
    const record_id = this.fieldValue?.id ?? -1;
    const isSOTrx = this.dataStored.data.IsSOTrx ? this.dataStored.data.IsSOTrx === 'Y' : undefined;
    this.subscriptions.push(
      this.uiCreatorService
        .zoomAcross(this.data.details.tableName, this.data.details.keyColumn, record_id, isSOTrx)
        .subscribe({
          next: (dataWs) => this.doZoomAcross(dataWs, record_id),
          error: (err) => {
            this.isZoom = false;
          },
        })
    );
  }

  refreshZoomInfo(): void {
    let record_id = -1;
    if (this.fieldValue) {
      record_id = this.fieldValue.id;
    }
    this.zoomInfo.record_id = record_id;
    this.zoomInfo.dataUUID = this.data.details.keyColumn.split('.')[1] + ',' + record_id;
    if (!this.zoomInfo.children && this.zoomInfo.record_id !== -1) {
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.selectZoomChange,
        item: {
          container: null,
          dataStoreKey: null,
          zoomInfo: this.zoomInfo,
        },
      });
    }
  }

  setNewData(dataStored: DataStore, isInit = false): void {
    if (!dataStored.isCopied) {
      this.isSetMandatoryValue = false;
    }
    super.setNewData(dataStored, isInit);
    if (this.autoComplete) {
      this.autoComplete.setMandatoryCss(this.mandatoryCss);
      this.checkAccordionAutocomplete();
      this.checkLabelClick();
    }
  }

  checkAccordionAutocomplete(): void {
    if (this.DOMParentComponent?.DOMParentComponent?.isAccordion) {
      this.DOMParentComponent.DOMParentComponent.setMandatory();
    }
  }

  onSiblingUpdate(event: any): void {
    if (event?.refreshZoom) {
      this.resetAutocompleteFromZoom(event.id);
    } else if (event && event.type === IupicsTypeEvent.expandEvent) {
      if (this.editViewParent?.editTabs?.length > 1) {
        for (const tab of this.editViewParent.editTabs) {
          tab.expandTab();
        }
        // this.editViewParent.smartButtons.resizeSmartButton(event);
      }
    } else if (event && event.type === IupicsTypeEvent.collapseEvent) {
      if (this.editViewParent?.editTabs?.length > 1) {
        for (const tab of this.editViewParent.editTabs) {
          tab.collapseTab();
        }
        // this.editViewParent.smartButtons.resizeSmartButton(event);
      }
    }
  }

  showConflictPanel(ev: any): void {
    ev.stopPropagation();
    this.opConflict.toggle(ev);
  }

  setParentContainer(): void {
    if (this.container instanceof SpecificWindowUiComponent || this.container instanceof ProcessUiComponent) {
      this.parentContainer = this.container;
    } else if (this.container) {
      const editView = this.container.DOMChildrenComponent.find(
        (DOMChild: any) => DOMChild instanceof EditViewUiComponent && DOMChild.tabId === this.data.tabId
      ) as EditViewUiComponent;
      this.parentContainer = editView;
    }
  }

  showSearchPanel(): void {
    this.setParentContainer();
    let url = null;
    if (this.data.urlSearch) {
      url = this.data.urlSearch;
    } else if (this.data?.details?.entityID) {
      url = '/Field/Search?id=' + this.data.details.entityID + '&type=0';
    }
    if (url) {
      this.subscriptions.push(
        this.uiCreatorService.getFieldsData(url).subscribe((searchColumns) => {
          this.data.searchColumns = searchColumns;
          this.lockFilterPanel();
          this.displaySearch = true;
        })
      );
    }
  }

  hideSearchPanel(): void {
    if (this.isProcessingFilterResponse) {
      return;
    }
    this.filterCtx.unregisterGrid(this.gridTab);
    this.unlockFilterPanel();
    this.displaySearch = false;
  }

  showFormPanel(formID): void {
    if (this.data.isAccount) {
      this.container['accountEditor_ColumnSearch'] = this.data.columnName;
    }
    this.updateModalDisplay(ModalDisplayRequest.Form(true, formID, this));
  }

  onInputClick(event: MouseEvent): void {
    event.stopPropagation();
    const rect = (this.elementRef?.nativeElement.firstChild as HTMLElement).getBoundingClientRect();
    this.top = rect.y + 26;
    this.left = rect.x;
    this.width = rect.width;
  }

  checkGridEditor(): void {
    if (this.suggestions != null) {
      const event = {
        overlayVisible: true,
        label: this.label,
        isLabelDisplay: this.isFieldDisplay,
        scrollHeight: this.scrollHeight,
        suggestions: this.suggestions,
        field: this.autoComplete.field,
        itemTemplate: this.autoComplete.itemTemplate,
        noResults: this.autoComplete.noResults,
        emptyMessage: this.autoComplete.emptyMessage,
        top: this.top,
        left: this.left,
        width: this.width,
        linkAutocomplete: this,
      };
      this.checkGridEditorEmitter.emit(event);
    }
  }

  createSpecificWindow(formId: number, sourceComponent: any): void {
    this.setParentContainer();
    this.subscriptions.push(
      this.uiCreatorService.getSpecificWindow(formId).subscribe((specificWindow) => {
        let component;
        if (
          specificWindow.angularClass &&
          specificWindow.angularClass.length > 0 &&
          specificWindow.angularClass !== 'default'
        ) {
          component = CacheManagerService.iupics_specific_window.get(specificWindow.angularClass);
        }
        if (!component) {
          component = CacheManagerService.iupics_specific_window.get('default');
        }
        this.vcrSpecific.clear();
        const componentRef = this.vcrSpecific.createComponent<SpecificWindowUiComponent>(component);
        this.specificWindowTitle = specificWindow.name;
        componentRef.instance.name = specificWindow.name;
        componentRef.instance.title = specificWindow.title;
        componentRef.instance.description = specificWindow.description;
        componentRef.instance.help = specificWindow.help;
        componentRef.instance.componentRef = componentRef;
        componentRef.instance.isModal = true;
        componentRef.instance.formId = formId;
        componentRef.instance.vcrwindow = this.vcrSpecific;
        componentRef.instance.parentComponent = this.parentContainer;
        componentRef.instance.sourceModal = this.specificModal;
        componentRef.instance.index = this.vcrSpecific.length - 1;
        componentRef.instance.sourceComponentData = sourceComponent?.itemData ?? sourceComponent;
        componentRef.instance.sourceComponent =
          sourceComponent; /**dupliqué si pas d'itemdata mais trop de refactoring sur scout */
        componentRef.instance.closeModalEmitter.subscribe(() =>
          this.updateModalDisplay(ModalDisplayRequest.Form(false))
        );
        this.componentRefs.push(componentRef);
      })
    );
  }

  /*
   * emailEditor: { display: { key: 'displayEmailEditor', value: displayEmailEditor }}
   * joinFilesPanel: { display: { key: 'displayJoinFilesPanel', value: displayJoinFilesPanel }}
   * processModal: { display: { key: 'displayProcessUI', value: displayProcessUI }, id: { key: 'processId', value: processId } }
   * formModal: { display: { key: 'displayFormUI', value: displayFormUI }, id: { key: 'formId', value: formId } }
   * sourceComponent : déterminer si autocomplete ou button
   */
  updateModalDisplay({ display, id }: ModalDisplayRequest): void {
    this.customFormModalBodyCss =
      display.key && display.key === 'displayFormUI'
        ? { 'iu-modal-body': { overflow: 'auto', height: 'calc(100% - 25px)' } }
        : undefined;
    this[display.key] = display.value;
    if (display.key === 'displayFormUI') {
      // the form panel can create records: do not serve a list cached before it
      this.#urlListSearchCache.clear();
    }
    if (id && display.value === true) {
      this[id.key] = id.value;
      if (id.key !== 'processId') {
        setTimeout(() => {
          // timeout le temps d'afficher le vcr
          this.lockFilterPanel();
          this.createSpecificWindow(id.value, display.sourceComponent);
        }, 500);
      }
    } else {
      this.unlockFilterPanel();
      this.customFormModalBodyCss = undefined;
    }
  }

  private updateLabel(): void {
    if (!this.data || !this.label) {
      return;
    }

    if (this.label.indexOf('_From') >= 0) {
      this.label = this.label.replace('_From', this.#translateService.instant('ranged-value.calendar.from'));
    } else if (this.label && this.label.indexOf('_To') >= 0) {
      this.label = this.label.replace('_To', this.#translateService.instant('ranged-value.calendar.to'));
    }
  }

  private updateFieldType(): void {
    if (this.data) {
      this.fieldType =
        this.isForInfoWindow && this.isAdvancedSearch ? CompiereDataFieldType.COLUMN_INFO : this.fieldType;
    }
  }

  private updateAdvancedSearch(): void {
    if (this.data) {
      this.isAdvancedSearch = !!this.data.urlSearch;
    }
  }

  private updateAccountSearch(): void {
    if (this.data) {
      this.isAccountSearch = !!this.data.isAccount;
    }
  }

  private updateFieldTypeForm(): void {
    if (this.data?.fieldTypeForm) {
      this.fieldType = CompiereDataFieldType.FORM_ITEM;
    }
  }

  private updateStandalone(): void {
    if (
      this.isStandalone &&
      (this.isMandatory || this.data?.isMandatory) &&
      this.data.items &&
      this.data.items.length > 0
    ) {
      this.fieldValue = this.data.items[0];
    }
  }

  private handleMandatoryField(): void {
    if (this.fieldValue) {
      this.isSetMandatoryValue = true;
    } else if (this.shouldFetchAutocompleteData()) {
      this.fetchAutocompleteData();
      this.isSetMandatoryValue = true;
    }
    this.mandatoryCss = ' iu-field-mandatory ';
  }

  private handleNonMandatoryField(): void {
    this.mandatoryCss = ' iu-field ';
  }

  private shouldFetchAutocompleteData(): boolean {
    return Boolean(
      (!this.data.defaultValue || this.isSetDefaultValue) &&
        !this.isSetMandatoryValue &&
        this.data &&
        this.data.urlList &&
        (!this.data.items || this.data.items.length <= 0 || this.data.validationCode)
    );
  }

  private fetchAutocompleteData(): void {
    if (this.fieldType !== CompiereDataFieldType.FIELD) {
      let validationCode;
      if (this.data.validationCode) {
        validationCode = LogicEvaluator.replaceVariables(
          this.data.validationCode,
          this.connectorService.getIupicsUserContext(),
          this.getCurrentContext(),
          false,
          false,
          !this.isInUniversalFilter,
          this.editViewService?.getDataColumns('referenceID')
        );
      }
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      const entityId = /*this.fieldType === CompiereDataFieldType.FIELD ? this.data.fieldId :*/ this.data.columnId;
      this.subscription = this.store
        .getAutocompleteData(
          this.fieldType,
          entityId,
          this.data.isSearch,
          null,
          validationCode,
          this.isInUniversalFilter
        )
        .subscribe((dataWs) => {
          this.suggestions = [];
          this.data.items = dataWs;
          if (
            this.dataStored.status === DataStoreStatus.NEWRECORD &&
            this.data.items.length === 1 &&
            (this.data.columnName !== 'AD_Client_ID' ||
              this.data.defaultValue === undefined ||
              (this.data.fromRange && this.data.defaultValue2 === undefined))
          ) {
            this.updateStore(this.data.items[0], [], true);
          }
          if (this.autoComplete) {
            this.autoComplete.setMandatoryCss(this.mandatoryCss);
          }
          this.#cdr.markForCheck();
        });
    }
  }

  private handleSearch(event: { originalEvent: Event; query: string }, forceRefresh: boolean): void {
    const regex = '.*'.concat(
      Utils.cleanUpSpecialChars(event.query || '', true)
        .split(' ')
        .join('.*'),
      '.*'
    );
    if (!this.data['events']) {
      // an explicit refresh is the context menu's own action: it must not be swallowed by the right-click flag
      if (forceRefresh) {
        this.isContextMenu = false;
      }
      if (!this.isContextMenu) {
        if (
          this.data?.urlList &&
          (forceRefresh ||
            this.data.validationCode ||
            event.query?.length > 0 ||
            ((event.query === null || event.query === undefined || event.query.length === 0) &&
              this.itemSuggestionsCache === null) ||
            !this.data.items ||
            this.data.items.length <= 0)
        ) {
          this.handleSearchWithUrlList(event, forceRefresh);
        } else {
          this.handleSearchWithoutUrlList(event, regex);
        }
      } else {
        this.autoComplete.loading = false;
        this.isContextMenu = false;
      }
    }
  }

  private handleSearchWithUrlList(event: { originalEvent: Event; query: string }, forceRefresh = false): void {
    let validationCode;
    if (this.data.validationCode) {
      validationCode = LogicEvaluator.replaceVariables(
        this.data.validationCode,
        this.connectorService.getIupicsUserContext(),
        this.getCurrentContext(),
        false,
        false,
        !this.isInUniversalFilter,
        this.editViewService?.getDataColumns('referenceID')
      );
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const entityId = this.fieldType === CompiereDataFieldType.FIELD ? this.data.fieldId : this.data.columnId;
    const cacheKey = `${entityId}_${event.query ?? ''}_${validationCode ?? ''}`;
    if (forceRefresh) {
      // explicit refresh (context menu): drop every cached query of this field, not only this key
      this.#urlListSearchCache.clear();
    }
    if (this.#urlListSearchCache.has(cacheKey)) {
      // copy: handleSearchResults keeps it as data.items, later mutations must not reach the cache
      this.handleSearchResults(event, [...this.#urlListSearchCache.get(cacheKey)]);
      return;
    }
    const obsIdentifier = `Autocomplete_${cacheKey}`;
    this.subscription = this.#cacheManagerService
      .getSharableObs(
        this.store.getAutocompleteData(
          this.fieldType,
          entityId,
          this.data.isSearch,
          event.query,
          validationCode,
          this.isInUniversalFilter
        ),
        obsIdentifier
      )
      .subscribe((dataWs) => {
        this.#cacheSearchResult(cacheKey, dataWs);
        this.handleSearchResults(event, dataWs);
      });
  }

  #cacheSearchResult(cacheKey: string, dataWs: any[]): void {
    if (this.#urlListSearchCache.has(cacheKey)) {
      this.#urlListSearchCache.delete(cacheKey);
    } else if (this.#urlListSearchCache.size >= AutocompleteUiComponent.URL_LIST_SEARCH_CACHE_LIMIT) {
      const oldestKey = this.#urlListSearchCache.keys().next().value;
      this.#urlListSearchCache.delete(oldestKey);
    }
    // store a copy, dataWs itself becomes data.items
    this.#urlListSearchCache.set(cacheKey, Array.isArray(dataWs) ? [...dataWs] : dataWs);
  }

  private handleSearchWithoutUrlList(event: { originalEvent: Event; query: string }, regex: string): void {
    if (this.itemSuggestionsCache !== null && this.itemSuggestionsCache.length > 0) {
      this.data.items = this.itemSuggestionsCache;
    }

    this.suggestions = [];
    if ((!event.query || event.query === '') && !this.isMandatory) {
      if (!this.isStandalone) {
        this.suggestions.push({ id: -2, displayValue: '' });
      }
      if (this.data.items) {
        this.suggestions.push(...this.data.items);
      }
    } else {
      this.addMatchingSuggestions(regex);
    }
    if ((this.isAdvancedSearch || this.isAccountSearch) && this.suggestions.length > this.limitSuggestions) {
      this.suggestions = this.suggestions.splice(0, this.limitSuggestions);
      this.suggestions.push({
        id: -1,
        displayValue: this.#translateService.instant('generic.moreResults'),
      });
    }

    if (!this.DOMParentComponent) {
      this.checkGridEditor();
    }
  }

  private addMatchingSuggestions(regex: string): void {
    for (const element of this.data.items) {
      const propose = element;
      if (this.data.isSearch || RegExp(regex).exec(Utils.cleanUpSpecialChars(propose.displayValue))) {
        this.suggestions.push(propose);
      }
    }
  }

  private handleSearchResults(event: { originalEvent: Event; query: string }, dataWs: any[]): void {
    this.suggestions = [];
    this.data.items = dataWs;

    if ((!event.query || event.query === '') && !this.isMandatory) {
      if (!this.isStandalone) {
        this.suggestions.push({ id: -2, displayValue: '' });
      }
      if (this.data.items) {
        for (const propose of this.data.items) {
          this.suggestions.push(propose);
        }
      }
    } else if (this.data.items) {
      for (const propose of this.data.items) {
        this.suggestions.push(propose);
      }
      this.itemSuggestionsCache = [...this.data.items];
    }
    this.addMoreResultsSuggestionIfNeeded();

    if (!this.DOMParentComponent) {
      this.checkGridEditor();
    }

    this.handleSingleResult(dataWs, event.query);
    this.cacheItemSuggestionsIfNeeded(dataWs, event.query);
    this.#cdr.markForCheck();
  }

  private addMoreResultsSuggestionIfNeeded(): void {
    if ((this.isAdvancedSearch || this.isAccountSearch) && this.suggestions.length > this.limitSuggestions) {
      this.suggestions = this.suggestions.splice(0, this.limitSuggestions);
      this.suggestions.push({
        id: -1,
        displayValue: this.#translateService.instant('generic.moreResults'),
      });
    }
  }

  private handleSingleResult(dataWs: any[], query: string): void {
    if (dataWs.length === 1 && this.isMandatory && !this.autoComplete.overlayVisible && !this.fieldValue) {
      this.fieldValue = this.data.items[0];
      super.dataChange(this.fieldValue);
    } else if (dataWs.length > 0) {
      const suggestion = dataWs.find((i: any) => i.displayValue === query);
      if (!isNil(suggestion) && !this.fieldValue) {
        this.fieldValue = suggestion;
        super.dataChange(this.fieldValue);
      }
    }
  }

  private cacheItemSuggestionsIfNeeded(dataWs: any[], query: string): void {
    if (query !== null && query !== undefined && query.length === 0) {
      if (this.data.items) {
        this.itemSuggestionsCache = [...this.data.items];
      }
    }
  }

  private doZoomAcross(dataWs, record_id: number): void {
    if (dataWs && dataWs.length > 0) {
      this.zoomInfo = {
        // tableName: this.data.detailZoom.tableName,
        windowId: dataWs[0].Window_ID,
        dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
        record_id: record_id,
        children: dataWs.length > 1 ? dataWs.splice(0, dataWs.length - 1) : null,
        isInCurrentWindow: true,
      };

      // check whether a tab with zoom info tabid already exist
      const tabExist = this.isTabExist(parseInt(dataWs[0].Tab_ID, 10));
      if (tabExist || this.data.isParam || this.container instanceof SpecificWindowUiComponent) {
        this.openExistingTab(dataWs, record_id);
      } else if (!this.#config.isModuleEnable('apiz-monowindow')) {
        this.openNewTab(dataWs);
      }
    }
  }

  private isTabExist(zoomTabId: number): boolean {
    let tabExist = false;
    if (this.container?.DOMChildrenComponent) {
      let j = 1;
      while (!tabExist && this.container.DOMChildrenComponent.length > j) {
        if (
          this.container.DOMChildrenComponent[j].DOMComponent.instance instanceof EditViewUiComponent &&
          this.container.DOMChildrenComponent[j].tabId === zoomTabId
        ) {
          tabExist = true;
        }
        j++;
      }
    }

    return tabExist;
  }

  private openExistingTab(dataWS: any[], record_id: number): void {
    this.isZoom = false;
    this.zoomInfo.isInCurrentWindow = false;
    this.#urlParamsService.updateUrlParams({ initGridParams: null });
    this.#workspaceService.openTargetSearchEmt.emit({
      zoomInfo: this.zoomInfo,
      cat: { id: parseInt(dataWS[0].Window_ID, 10) },
      source: {
        id: record_id !== -1 ? dataWS[0].Record_ID : 'newRecord',
      },
    });
  }

  private openNewTab(dataWS: any[]): void {
    const windowId = parseInt(dataWS[dataWS.length - 1]['Window_ID'], 10);
    this.subscriptions.push(
      this.uiCreatorService.getWindow(windowId).subscribe((tabUI) => {
        const item: DynamicComponent = {
          container: this.container,
          DOMParentComponent: this.container,
          linkedComponents: [this],
          component: 'EditViewUiComponent',
          cssClass: 'iupics-blade-content',
          isCssOnComponent: false,
          tabId: tabUI.tabId,
          windowId: windowId,
          zoomInfo: this.zoomInfo,
        };
        this.componentEmitter.emit({
          type: IupicsTypeEvent.showEditView,
          item: item,
        });
      })
    );
  }

  getCurrentContext(dataStore?: DataStore, overrideChild?: boolean, forceRefresh?: boolean): object {
    return this.standaloneCtx?.() ?? super.getCurrentContext();
  }
}
